import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '@/app/store';
import {
  errorCleared,
  login,
  logout,
  selectAuthError,
  selectAuthUser,
  selectIsAdmin,
  selectIsAuthenticated,
  selectIsRestoringSession,
  selectIsSigningIn,
} from '../store/authSlice';
import type { AuthenticatedUser, LoginCredentials, UserRole } from '../types/auth.types';

export interface UseAuthResult {
  readonly user: AuthenticatedUser | null;
  readonly isAuthenticated: boolean;
  readonly isRestoringSession: boolean;
  readonly isSigningIn: boolean;
  readonly isAdmin: boolean;
  readonly error: string | null;
  readonly signIn: (credentials: LoginCredentials) => Promise<boolean>;
  readonly signOut: () => Promise<void>;
  readonly clearError: () => void;
  readonly hasRole: (role: UserRole) => boolean;
}

/** Single entry point features use to read session state and drive sign in/out. */
export function useAuth(): UseAuthResult {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const user = useAppSelector(selectAuthUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isRestoringSession = useAppSelector(selectIsRestoringSession);
  const isSigningIn = useAppSelector(selectIsSigningIn);
  const isAdmin = useAppSelector(selectIsAdmin);
  const error = useAppSelector(selectAuthError);

  const signIn = useCallback(
    async (credentials: LoginCredentials): Promise<boolean> => {
      const result = await dispatch(login(credentials));
      if (login.fulfilled.match(result)) queryClient.clear();
      return login.fulfilled.match(result);
    },
    [dispatch, queryClient]
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await dispatch(logout());
    } finally {
      // Query keys intentionally describe resources, not users. Purge every resource at
      // the session boundary so one account can never see another account's cached data.
      queryClient.clear();
    }
  }, [dispatch, queryClient]);

  const clearError = useCallback((): void => {
    dispatch(errorCleared());
  }, [dispatch]);

  const hasRole = useCallback((role: UserRole): boolean => user?.roles.includes(role) ?? false, [user]);

  return {
    user,
    isAuthenticated,
    isRestoringSession,
    isSigningIn,
    isAdmin,
    error,
    signIn,
    signOut,
    clearError,
    hasRole,
  };
}
